import { useTables } from '@/hooks';
import { ENCRYPT } from '@/constants';
import pacote from '../../../package.json';
import { openDB as idb, deleteDB as idbDeleteDatabase, IDBPDatabase } from 'idb';
import { useEvents } from '../use-events/useEvents';
import { ProductProps, TableTypes, tableKeys } from '@/types';
import { unregisterServiceWorker } from '@/shared/serviceWorkFunctions';

export type Tables = (typeof tableKeys)[number];
const listTables: Tables[] = [...tableKeys];
const TABLE_ALIASES: Record<string, Tables> = {
  preco_novo: 'preco',
  saldo_flex_novo: 'saldo_flex',
  campanhaClientes: 'campanha_cliente',
};

/** Mesma regra que `setOnDB`: não aceita array/objeto vazio (exceto quando `force` no write único). */
export const isValidDbPayload = (data: any): boolean =>
  !!(
    data &&
    typeof data === 'object' &&
    !(Array.isArray(data) && data.length === 0) &&
    !(typeof data === 'object' && !Array.isArray(data) && Object.keys(data).length === 0)
  );

export const resolveTableNameForWrite = (name: string): Tables | null => {
  const normalized = (TABLE_ALIASES[name] || name) as Tables;
  return listTables.includes(normalized) ? normalized : null;
};

/**
 * Uma única transação readwrite em todas as lojas listadas: ou todas as escritas concluem ou nenhuma altera o IDB.
 * Não inclui `imagens` (outro banco) nem `produtos` (somente leitura derivada).
 */
export const setMultipleOnDBAtomic = async (writes: Array<{ name: Tables; data: any }>): Promise<void> => {
  const resolved = writes
    .map((w) => {
      const n = resolveTableNameForWrite(String(w.name));
      return n ? ({ name: n, data: w.data } as const) : null;
    })
    .filter(Boolean) as Array<{ name: Tables; data: any }>;

  const main = resolved.filter((w) => w.name !== 'produtos' && w.name !== 'imagens');
  if (!main.length) return;

  for (const { name, data } of main) {
    if (!isValidDbPayload(data)) {
      throw new Error(`Dados inválidos ou vazios para salvar em ${name}`);
    }
  }

  const currDB = await openDBConnection();
  try {
    const codedNames = [...new Set(main.map((w) => toCoded(w.name)))];
    const tx = currDB.transaction(codedNames, 'readwrite');

    for (const { name, data } of main) {
      const storeName = toCoded(name);
      const store = tx.objectStore(storeName);
      const allKeys = await store.getAllKeys();
      if (allKeys.length) {
        await store.delete(allKeys[allKeys.length - 1]);
      }
      await store.put({ data, keyPath: storeName });
    }

    await tx.done;
  } finally {
    currDB.close();
  }
};

// localStorage.clear();

// // Limpar SessionStorage
// sessionStorage.clear();

// // Limpar IndexedDB
// indexedDB.databases().then(dbs => {
//   dbs.forEach(db => {
//     indexedDB.deleteDatabase(db.name || '');
//   });
// });

export const toCoded = (str: string, forceEncrypt?: boolean, noReplace?: boolean): string => {
  if (!str || (!ENCRYPT && !forceEncrypt)) return str;

  try {
    const filteredStr = noReplace ? str : str.replace(/[^a-zA-Z]/g, '');
    const encoder = new TextEncoder();
    const uint8Array = encoder.encode(filteredStr);
    const binaryStr = String.fromCharCode(...uint8Array);
    return window.btoa(binaryStr).replace(/[^a-zA-Z]/g, '');
  } catch (error) {
    return noReplace ? str : str.replace(/[^a-zA-Z]/g, '');
  }
};

const DB_NAME = toCoded('BATE_FORTE');
const DB_IMAGES_NAME = toCoded('BATE_FORTE_IMAGES');
const DB_VERSION = Number(pacote.version.replace('.', ''));
const IMAGES_DB_PRODUCT_KEY_MIN_VERSION = 15;
const IMAGES_DB_OPEN_VERSION = Math.max(DB_VERSION, IMAGES_DB_PRODUCT_KEY_MIN_VERSION);
const RETRY_DELAY = 300;
const MAX_RETRIES = 5;

/** Tempo extra após `deleteDatabase` resolver (mobile/PWA costuma precisar de um tick a mais). */
const IDB_DELETE_SETTLE_MS = 450;

const settleAfterIdbDelete = () => new Promise<void>((r) => setTimeout(r, IDB_DELETE_SETTLE_MS));

export const deleteDB = async (): Promise<void> => {
  try {
    await idbDeleteDatabase(DB_NAME, {
      blocked() {
        console.warn('[IDB] Exclusão do banco principal bloqueada até outras conexões fecharem');
      },
    });
    await settleAfterIdbDelete();
  } catch (error) {
    console.error('Erro ao deletar banco de dados:', error);
  }
};

export const deleteImagesDB = async (): Promise<void> => {
  try {
    await idbDeleteDatabase(DB_IMAGES_NAME, {
      blocked() {
        console.warn('[IDB] Exclusão do banco de imagens bloqueada até outras conexões fecharem');
      },
    });
    await settleAfterIdbDelete();
  } catch (error) {
    console.error('Erro ao deletar banco de imagens:', error);
  }
};

/**
 * Apaga os dois IndexedDB do app em sequência e só retorna quando o `idb` concluir (Promise real).
 * Usado no reset forçado por versão; retorna `false` se alguma exclusão falhar.
 */
export const deleteAppIndexedDatabasesCompletely = async (): Promise<boolean> => {
  let ok = true;
  try {
    await idbDeleteDatabase(DB_NAME, {
      blocked() {
        console.warn('[IDB] reset: banco principal bloqueado, aguardando fechamento de conexões');
      },
    });
    await settleAfterIdbDelete();
  } catch (e) {
    ok = false;
    console.error('[IDB] Falha ao excluir banco principal:', e);
  }
  try {
    await idbDeleteDatabase(DB_IMAGES_NAME, {
      blocked() {
        console.warn('[IDB] reset: banco de imagens bloqueado, aguardando fechamento de conexões');
      },
    });
    await settleAfterIdbDelete();
  } catch (e) {
    ok = false;
    console.error('[IDB] Falha ao excluir banco de imagens:', e);
  }
  return ok;
};

const createObjectStore = (db: IDBPDatabase, name: string): void => {
  if (name === 'imagens') return;

  const tableName = toCoded(name);
  if (!db.objectStoreNames.contains(tableName)) {
    db.createObjectStore(tableName, {
      keyPath: toCoded(name),
      autoIncrement: true,
    });
  }
};

const checkAndCreateMissingTables = async (): Promise<IDBPDatabase> => {
  let db = await idb(DB_NAME, DB_VERSION);
  const existingStores = Array.from(db.objectStoreNames);

  const missingTables = listTables.filter((tableName) => tableName !== 'imagens' && !existingStores.includes(toCoded(tableName)));

  if (missingTables.length > 0) {
    db.close();

    const newVersion = listTables.length + (Math.floor(Date.now() / 1000) % 1000);

    db = await idb(DB_NAME, newVersion, {
      upgrade(db) {
        listTables.forEach((name) => createObjectStore(db, name));
      },
    });
  }

  return db;
};

export const openDBConnection = async (): Promise<IDBPDatabase> => {
  try {
    let db = await idb(DB_NAME, DB_VERSION, {
      upgrade(db) {
        listTables.forEach((name) => createObjectStore(db, name));
      },
    });

    const existingStores = Array.from(db.objectStoreNames);
    const missingTables = listTables.filter(
      (tableName) => tableName !== 'imagens' && !existingStores.includes(toCoded(tableName))
    );

    if (missingTables.length > 0) {
      db.close();
      db = await checkAndCreateMissingTables();
    }

    return db;
  } catch (error) {
    console.error('Erro ao abrir conexão com banco de dados:', error);
    await deleteDB();
    await unregisterServiceWorker({});
    throw error;
  }
};

export const openImagesDBConnection = async (): Promise<IDBPDatabase> => {
  try {
    return await idb(DB_IMAGES_NAME, IMAGES_DB_OPEN_VERSION, {
      upgrade(db, oldVersion) {
        const storeName = toCoded('imagens');
        if (oldVersion < IMAGES_DB_PRODUCT_KEY_MIN_VERSION && db.objectStoreNames.contains(storeName)) {
          db.deleteObjectStore(storeName);
        }
        if (!db.objectStoreNames.contains(storeName)) {
          db.createObjectStore(storeName, { keyPath: 'COD_PRODUTO' });
        }
      },
    });
  } catch (error) {
    console.error('Erro ao abrir conexão com banco de imagens:', error);
    throw error;
  }
};

const handleGet = async <T extends Tables>(name: T, currDB: IDBPDatabase): Promise<TableTypes[T]> => {
  try {
    const transaction = currDB.transaction(toCoded(name), 'readonly');
    const store = transaction.objectStore(toCoded(name));
    const allKeys = await store.getAllKeys();
    const lastKey = allKeys[allKeys.length - 1];
    if (lastKey == null) {
      return [] as TableTypes[T];
    }

    const lastData = await store.get(lastKey as IDBValidKey);
    const resp = (lastData?.data ?? []) as TableTypes[T];
    return name === 'restricaoProdutoCliente' ? resp || [] : resp;
  } catch (error) {
    console.info(`Erro ao obter dados da tabela ${name}:`, error);
    return [] as TableTypes[T];
  }
};

export const useDbFunctions = () => {
  const { tables, setOnTables, isLoading } = useTables();

  const normalizeTableName = (name: string): Tables | null => {
    const normalized = (TABLE_ALIASES[name] || name) as Tables;
    return listTables.includes(normalized) ? normalized : null;
  };

  const handleProductList = async <T extends Tables>(
    _: T,
    currDB: IDBPDatabase,
    COD_CLIENTE?: number
  ): Promise<TableTypes[T]> => {
    try {
      const [list, restrictedList] = await Promise.all([
        handleGet('produtos', currDB),
        handleGet('restricaoProdutoCliente', currDB),
      ]);

      const restrictions = new Set(
        restrictedList?.filter((r) => r?.COD_CLIENTE === COD_CLIENTE)?.map((r) => r?.COD_PRODUTO) || []
      );

      return list?.filter((p) => !restrictions.has(p.COD_PRODUTO)) as TableTypes[T];
    } catch (error) {
      console.error('Erro ao processar lista de produtos:', error);
      return [] as TableTypes[T];
    }
  };

  const closeDBConnection = (db: IDBPDatabase | undefined): void => {
    if (db) {
      db.close();
    }
  };

  // const getImagesFromDB = async () => {
  //   try {
  //     const currDB = await openImagesDBConnection();
  //     const transaction = currDB.transaction(toCoded('imagens'), 'readonly');
  //     const store = transaction.objectStore(toCoded('imagens'));
  //     const rows = await store.getAll();
  //     await transaction.done;
  //     closeDBConnection(currDB);
  //     if (!rows?.length) return null;
  //     const out: Record<number, string> = {};
  //     for (const row of rows) {
  //       const cod = row?.COD_PRODUTO;
  //       const imagem = row?.imagem;
  //       if (cod != null && Number.isFinite(Number(cod)) && typeof imagem === 'string') {
  //         out[Number(cod)] = imagem;
  //       }
  //     }
  //     return Object.keys(out).length ? out : null;
  //   } catch (error) {
  //     console.error('Erro ao obter imagens do banco:', error);
  //     return null;
  //   }
  // };

  const getImageFromDB = async (COD_PRODUTO: number): Promise<string | null> => {
    try {
      const currDB = await openImagesDBConnection();
      const transaction = currDB.transaction(toCoded('imagens'), 'readonly');
      const store = transaction.objectStore(toCoded('imagens'));
      const row = await store.get(COD_PRODUTO);
      await transaction.done;
      closeDBConnection(currDB);
      return row?.imagem ?? null;
    } catch (error) {
      console.error('Erro ao obter imagem do banco:', error);
      return null;
    }
  };

  const setImagesOnDB = async (data: any): Promise<void> => {
    if (!isValidDbPayload(data)) return;

    const rows: Array<{ COD_PRODUTO: number; imagem: string }> = [];
    if (Array.isArray(data)) {
      for (const item of data) {
        const cod = item?.COD_PRODUTO;
        const raw = item?.imagem;
        if (cod == null || raw == null) continue;
        const imagem = typeof raw === 'string' ? raw.replace(/^data:.*;base64,/i, '') : String(raw);
        rows.push({ COD_PRODUTO: Number(cod), imagem });
      }
    } else if (data && typeof data === 'object') {
      for (const [k, v] of Object.entries(data)) {
        const cod = Number(k);
        if (!Number.isFinite(cod) || v == null) continue;
        const imagem = typeof v === 'string' ? v.replace(/^data:.*;base64,/i, '') : String(v);
        rows.push({ COD_PRODUTO: cod, imagem });
      }
    }

    try {
      const currDB = await openImagesDBConnection().catch((err) => {
        throw new Error(`Erro ao abrir conexão com banco de dados para salvar imagens: ${err?.message ?? err}`);
      });
      const transaction = currDB.transaction(toCoded('imagens'), 'readwrite');
      const store = transaction.objectStore(toCoded('imagens'));

      await store.clear().catch((err) => {
        throw new Error(`Erro ao limpar imagens do banco de dados: ${err?.message ?? err}`);
      });

      for (const row of rows) {
        await store.put(row).catch((err) => {
          throw new Error(`Erro ao salvar imagem COD_PRODUTO=${row.COD_PRODUTO} no banco: ${err?.message ?? err}`);
        });
      }

      await transaction.done.catch((err) => {
        throw new Error(`Erro ao fechar transação do banco de dados para salvar dados em imagens: ${err?.message ?? err}`);
      });

      closeDBConnection(currDB);
    } catch (error) {
      console.error('Erro ao salvar imagens no banco:', error);
      return Promise.reject(error);
    }
  };

  const getImagesFromDB = async (): Promise<TableTypes['imagens']> => {
    let currDB: IDBPDatabase | undefined;
    try {
      currDB = await openImagesDBConnection();
      const transaction = currDB.transaction(toCoded('imagens'), 'readonly');
      const store = transaction.objectStore(toCoded('imagens'));
      const rows = await store.getAll();
      await transaction.done;
      const out: Record<number, string> = {};
      if (Array.isArray(rows)) {
        for (const row of rows) {
          const cod = row?.COD_PRODUTO;
          if (cod == null || !Number.isFinite(Number(cod))) continue;
          const raw = row?.imagem;
          if (raw == null) continue;
          out[Number(cod)] = typeof raw === 'string' ? raw.replace(/^data:.*;base64,/i, '') : String(raw);
        }
      }
      return Object.keys(out).length ? out : null;
    } catch (error) {
      console.error('Erro ao obter imagens do banco:', error);
      return null;
    } finally {
      closeDBConnection(currDB);
    }
  };

  const getOnDB = async <T extends Tables>(
    name: T,
    client?: number,
    retryCount: number = 0,
    force: boolean = false,
    enableFillTables: boolean = true
  ): Promise<TableTypes[T]> => {
    const safeName = normalizeTableName(String(name));
    if (!safeName) {
      console.warn(`Tabela desconhecida solicitada: ${String(name)}`);
      return [] as TableTypes[T];
    }

    if (isLoading.current[safeName] && !force) {
      if (retryCount >= MAX_RETRIES) {
        // Evita quebrar a UI em cenários de disputa/hidratação lenta.
        console.warn(`Tempo limite excedido ao carregar ${safeName}. Retornando cache/fallback.`);
        const cachedData = tables.current?.[safeName];
        if (cachedData != null) {
          return cachedData as TableTypes[T];
        }
        return [] as TableTypes[T];
      }
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
      return getOnDB(safeName as T, client, retryCount + 1, force);
    }

    isLoading.current[safeName] = true;

    try {
      const cachedData = tables.current?.[safeName];
      const hasCachedData =
        Array.isArray(cachedData)
          ? cachedData.length > 0
          : !!cachedData && (typeof cachedData !== 'object' || Object.keys(cachedData).length > 0);

      if (hasCachedData && !force) {
        isLoading.current[safeName] = false;
        return cachedData as TableTypes[T];
      }

      if (safeName === 'imagens') {
        const images = (await getImagesFromDB()) as TableTypes['imagens'];
        setOnTables({}, 'imagens', force);
        return images;
      }

      const currDB = await openDBConnection();
      let list = null

      if (safeName === 'produtos'){
        list = await handleProductList(safeName, currDB, client)
      } else {
        list = await handleGet(safeName, currDB)
      }

      if (safeName === 'dicionario_produtos') {
        const isArray = Array.isArray(list);
        const isEmptyObject =
          list && typeof list === 'object' && !Array.isArray(list) && Object.keys(list as object).length === 0;

        // Se não existir, estiver vazio ou vier em formato inesperado (ex.: array), reconstrói o dicionário.
        // Nota: `produtos` não é persistido via setOnDB; o fallback útil é montar a partir de `catalogo`.
        if (!list || isArray || isEmptyObject) {
          const produtos = await handleProductList('produtos', currDB, client);
          let dicionario = (produtos || []).reduce(
            (acc, i) => ({
              ...acc,
              [i.COD_PRODUTO]: i,
            }),
            {} as Record<number, ProductProps>
          );

          if (Object.keys(dicionario).length === 0) {
            const catalogo = (await handleGet('catalogo', currDB)) as ProductProps[] | unknown;
            if (Array.isArray(catalogo) && catalogo.length) {
              dicionario = catalogo.reduce((acc, item) => {
                if (item?.COD_PRODUTO == null) return acc;
                acc[item.COD_PRODUTO] = {
                  ...item,
                  PRECO: Array.isArray(item.PRECO) ? item.PRECO : [],
                };
                return acc;
              }, {} as Record<number, ProductProps>);
            }
          }

          list = dicionario as unknown as TableTypes[T];
        }
      }

      currDB.close();
      if (enableFillTables && name === 'catalogo' /* || name === 'categoria' */) setOnTables([{}], safeName, force);
      else if (enableFillTables) setOnTables(list, safeName, force);

      return list as TableTypes[T];
    } catch (error) {
      console.error(`Erro ao obter dados de ${safeName}:`, error);
      return [] as TableTypes[T];
    } finally {
      isLoading.current[safeName] = false;
    }
  };

  const setOnDB = async (data: any, name: Tables, force: boolean = false): Promise<void> => {
    if (!name) {
      return Promise.reject(new Error('Chave ausente'));
    };

    if (!isValidDbPayload(data) && !force) {
      return Promise.reject(new Error('Dados inválidos'));
    };
    
    if (name === 'produtos') {
      return Promise.reject(new Error('Não é possível salvar dados em produtos'));
    };

    try {
      if (name === 'imagens') {
        return setImagesOnDB(data);
      }

      const currDB = await openDBConnection().catch(err => {
        throw new Error(`Erro ao abrir conexão com banco de dados para salvar dados em ${name}: ${err?.message ?? err}`);
      });

      const transaction = currDB.transaction(toCoded(name), 'readwrite');
      const store = transaction.objectStore(toCoded(name));

      const allKeys = await store.getAllKeys().catch(err => {
        throw new Error(`Erro ao obter chaves do banco de dados para salvar dados em ${name}: ${err?.message ?? err}`);
      });

      if (allKeys.length) {
        await store.delete(allKeys[allKeys.length - 1]).catch(err => {
          throw new Error(`Erro ao deletar dados do banco de dados para salvar dados em ${name}: ${err?.message ?? err}`);
        });
      }

      await store.put({ data, keyPath: toCoded(name) }).catch(err => {
        throw new Error(`Erro ao salvar dados no banco de dados para salvar dados em ${name}: ${err?.message ?? err}`);
      });

      await transaction.done.catch(err => {
        throw new Error(`Erro ao fechar transação do banco de dados para salvar dados em ${name}: ${err?.message ?? err}`);
      });
      
      closeDBConnection(currDB);
    } catch (error) {
      console.error(`Erro ao salvar dados em ${name}:`, error);
      return Promise.reject(error);
    }
  };

  const reloadTables = async () => {
    const listToReload = []
    console.log({listTables});
    
    for (const table of listTables) {
      if (table === 'catalogo') continue;
      await getOnDB(table, undefined, undefined, true);
    }
  };

  const validateTable = async (table: Tables): Promise<boolean> => {
    try {
      const db = table !== 'imagens' ? await openDBConnection() : await openImagesDBConnection();
      const transaction = db.transaction(toCoded(table), 'readonly');
      const store = transaction.objectStore(toCoded(table));
      const total = await store.count();
      return !!total;
    }
    catch (error) {
      console.info(`Erro ao validar existência de dados na tabela ${table}:`, error);
      return false;
    }
  };

  return {
    getOnDB,
    setOnDB,
    validateTable,
    getImageFromDB,
    setMultipleOnDBAtomic,
    deleteDB,
    deleteImagesDB,
    reloadTables,
    closeDBConnection,
    // getImagesFromDB,
    setImagesOnDB,
  };
};
